'use client';

import { ChangeEvent, DragEvent, FC, useCallback, useEffect, useMemo } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ContentSlot } from '@telialt/core-cms-content/src/components/ContentSlot/ContentSlot';
import { ProductsCardsSkeleton } from '@telialt/core-eshop/src/components/ProductsCardsSkeleton/ProductsCardsSkeleton';
import { SessionProlongationModal } from '@telialt/core-nextjs/src/components/SessionProlongationModal/SessionProlongationModal';
import { ProductsFacetsDrawer } from '@telialt/core-search/src/Common/Facets/ProductsFacetsDrawer';
import { ProductsFacetsPanel } from '@telialt/core-search/src/Common/Facets/ProductsFacetsPanel';
import { ProductsSelectedFilters } from '@telialt/core-search/src/Common/Facets/ProductsSelectedFilters';
import { SortDropdown } from '@telialt/core-search/src/Common/SortDropdown/SortDropdown';
import { SEARCH_CATEGORY_PRODUCTS_RESULTS_PER_PAGE_COUNT } from '@telialt/core-search/src/constants';
import { useLoadCategoryProducts } from '@telialt/core-search/src/hooks/useLoadCategoryProducts';
import { usePageOffset } from '@telialt/core-search/src/hooks/usePageOffset';
import { ProductSearchItem, SearchFacet, SearchFilterValue } from '@telialt/core-search/src/model';
import { cleanupFiltersWithoutResults } from '@telialt/core-search/src/utils/filtersCleanup';
import { extractANDFilters } from '@telialt/core-search/src/utils/filtersUtils';
import { useProductsCompare } from '@telialt/eshop-comparison/src/hooks/useProductsCompare';
import { trackSelectItemGA, trackViewItemListGA } from '@telialt/gtm-tracking/src/events';
import '@telialt/utils/src/polyfills/string-replace-all';
import { TeliaSegment } from '@telialt/utils/src/segment';

import { ESHOP_CATEGORY_TEST_ID } from '../../constants/test-ids';
import {
    getCategoryPageTitleSuffix,
    readCategoryPageNumber,
    readCategorySortParam,
} from '../../hooks/readCategorySearchParams';
import { useCategoryFilterValues } from '../../hooks/useCategoryFilterValues';
import { useCategoryLocation } from '../../hooks/useCategoryLocation';
import { useCategoryQuery } from '../../hooks/useCategoryQuery';
import { useCategorySortParam } from '../../hooks/useCategorySortParam';
import { Subcategory } from '../../models';
import { A11yProductsAnnouncer } from './A11yProductsAnnouncer/A11yProductsAnnouncer';
import { Banner } from './Banner/Banner';
import { BrandFilters } from './BrandFilters/BrandFilters';
import { CategoryDescription } from './CategoryDescription/CategoryDescription';
import {
    DescriptionWithContentSlotContainer,
    DrawerButtonContainer,
    FacetsContainer,
    PageGrid,
    PageTitleWrapper,
    ProductsAndFacetsContainer,
    SelectedFiltersContainer,
    SortFiltersWrapper,
    SortSelectContainer,
    VatPricesNote,
} from './CategoryPage.styles';
import { CategorySkeleton } from './CategorySkeleton/CategorySkeleton';
import { FacetsSkeleton } from './CategorySkeleton/CategorySkeleton.styles';
import { ComparedProducts } from './ComparedProducts/ComparedProducts';
import { LoginCTA } from './LoginCTA/LoginCTA';
import { ProductsItems } from './ProductsItems/ProductsItems';
import { SubcategoryCards } from './SubcategoryCards/SubcategoryCards';
import { TopBanner } from './TopBanner/TopBanner';

type Props = {
    segment: TeliaSegment;
    subcategories?: Subcategory[];
    isFallbackVersion?: boolean;
};

export const CategoryPage: FC<Props> = ({ segment, subcategories, isFallbackVersion }) => {
    const { t } = useTranslations();

    const { pathname, searchParams } = useCategoryLocation();

    const { categoryData, categoryDataIsLoading } = useCategoryQuery(pathname, segment);

    const initialPage = readCategoryPageNumber(searchParams);
    const { offset, pageNumber, changePageNumber } = usePageOffset(
        SEARCH_CATEGORY_PRODUCTS_RESULTS_PER_PAGE_COUNT,
        initialPage,
    );
    const handlePageChange = (page: number) => {
        // NOTE: behavior smooth causes to scroll to the middle of page after content is loaded for some reason
        window.scrollTo({ top: 0, behavior: 'instant' });
        changePageNumber(page);
    };

    useEffect(() => {
        const baseTitle = categoryData?.metadata?.metaElements?.find(
            (metaElement) => metaElement.name === 'title',
        )?.content;

        if (!baseTitle) {
            return;
        }

        const pageTitleSuffix = getCategoryPageTitleSuffix(pageNumber, t);
        document.title = pageTitleSuffix ? `${baseTitle} | ${pageTitleSuffix}` : baseTitle;
    }, [categoryData, pageNumber, t]);

    const initialSortParamValue = readCategorySortParam(searchParams);
    const { sortValue, sortFields, changeSort } = useCategorySortParam(initialSortParamValue);
    const handleSortChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = event.target.value;
        changeSort(selectedValue);
    };

    const { filters, setFilterValue, removeAllFilters, categoryPath } = useCategoryFilterValues(categoryData);

    const handleFilterChange = (fieldName: string, filter: SearchFilterValue) => {
        setFilterValue(products?.facets || [], fieldName, filter);
    };

    const handleRemoveFilter = (fieldName: string, filter?: SearchFilterValue) => {
        setFilterValue(products?.facets || [], fieldName, filter);
    };

    const handleRemoveAllFilters = () => {
        removeAllFilters();
    };

    let facets: SearchFacet[];

    const { data: products, isLoading: isProductsLoading } = useLoadCategoryProducts({
        offset,
        filters: extractANDFilters(cleanupFiltersWithoutResults(facets, filters)),
        sort: sortFields,
        limit: SEARCH_CATEGORY_PRODUCTS_RESULTS_PER_PAGE_COUNT,
        isEnabled: true,
        segment,
        isFallback: isFallbackVersion,
    });
    facets = products?.facets;

    const {
        compareProductsLimit,
        comparedProducts,
        addComparedProduct,
        removeComparedProduct,
        removeAllComparedProducts,
    } = useProductsCompare(categoryPath);

    const hasComparison = !!categoryData?.comparable;

    const compareHandler = (product: ProductSearchItem, isChecked: boolean) => {
        if (isChecked) {
            addComparedProduct(product);
        } else {
            removeComparedProduct(product.id);
        }
    };

    const handleDragStart = (event: DragEvent, product: ProductSearchItem) => {
        event.dataTransfer.setData('product', JSON.stringify(product));
    };

    const itemCategoryName = useMemo(() => {
        return subcategories?.find((c) => c.code === categoryData.code)?.name;
    }, [categoryData.code, subcategories]);

    useEffect(() => {
        if (isProductsLoading === false) {
            trackViewItemListGA({
                source: 'searchProducts',
                data: {
                    products: products?.items || [],
                    itemListName: categoryData.name,
                    itemCategory: itemCategoryName,
                },
            });
        }
    }, [categoryData.name, isProductsLoading, itemCategoryName, products?.items]);

    const handleItemClick = useCallback(
        (product: ProductSearchItem) => {
            trackSelectItemGA({
                source: 'searchProducts',
                data: { products: [product], itemCategory: itemCategoryName, itemListName: categoryData.name },
            });
        },
        [categoryData.name, itemCategoryName],
    );

    if (categoryDataIsLoading) {
        return <CategorySkeleton />;
    }
    const subcategoriesWithoutCurrent =
        subcategories?.filter((subcategory) => subcategory.code !== categoryData.code) || [];

    return (
        <div data-test={ESHOP_CATEGORY_TEST_ID.CATEGORY_PAGE_RESULTS_PRODUCTS}>
            <TopBanner currentCategoryName={categoryData?.name} currentCategoryUrl={categoryData?.url} />
            <LoginCTA />
            <SubcategoryCards subcategories={subcategoriesWithoutCurrent} />

            <PageGrid
                data-test={
                    segment === TeliaSegment.B2b
                        ? ESHOP_CATEGORY_TEST_ID.CATEGORY_B2B_PAGE
                        : ESHOP_CATEGORY_TEST_ID.CATEGORY_B2C_PAGE
                }
            >
                <Banner banner={categoryData.banner} />

                <PageTitleWrapper>
                    {!isFallbackVersion && (
                        <SortFiltersWrapper>
                            <DrawerButtonContainer>
                                {!!products && products.facets?.length > 0 && (
                                    <ProductsFacetsDrawer
                                        facets={products.facets}
                                        filters={filters}
                                        onChange={handleFilterChange}
                                        onRemoveAllFilters={handleRemoveAllFilters}
                                    />
                                )}
                            </DrawerButtonContainer>
                            <SortSelectContainer>
                                <SortDropdown
                                    value={sortValue}
                                    onChange={handleSortChange}
                                    dataTest={ESHOP_CATEGORY_TEST_ID.CATEGORY_PAGE_SORT_DROPDOWN}
                                />
                            </SortSelectContainer>
                        </SortFiltersWrapper>
                    )}
                </PageTitleWrapper>
                {!isFallbackVersion && (
                    <BrandFilters
                        categoryData={categoryData}
                        facets={facets}
                        filters={filters}
                        handleFilterChange={handleFilterChange}
                    />
                )}

                {segment === TeliaSegment.B2b && (
                    <VatPricesNote variant="additional-100">{t['eshop.category.vat_note']}</VatPricesNote>
                )}

                {!isFallbackVersion && !!products && Object.keys(filters).length > 0 && (
                    <SelectedFiltersContainer>
                        <ProductsSelectedFilters
                            facets={products?.facets || []}
                            filters={filters}
                            onRemoveFilter={handleRemoveFilter}
                            onRemoveAllFilters={handleRemoveAllFilters}
                        />
                    </SelectedFiltersContainer>
                )}

                <ProductsAndFacetsContainer $isFallback={isFallbackVersion}>
                    {!isFallbackVersion && isProductsLoading && !products?.facets?.length && (
                        <FacetsContainer>
                            <FacetsSkeleton />
                        </FacetsContainer>
                    )}
                    {!isFallbackVersion && products?.facets?.length > 0 && (
                        <FacetsContainer>
                            <ProductsFacetsPanel
                                facets={products.facets}
                                filters={filters}
                                onChange={handleFilterChange}
                            />
                        </FacetsContainer>
                    )}
                    <A11yProductsAnnouncer isLoading={isProductsLoading} products={products} />
                    <section aria-busy={isProductsLoading} aria-labelledby="category-title">
                        {isProductsLoading ? (
                            <ProductsCardsSkeleton />
                        ) : (
                            <ProductsItems
                                segment={segment}
                                categoryPath={categoryPath}
                                offset={offset}
                                pageNumber={pageNumber}
                                products={products}
                                currentPathname={pathname}
                                currentSearchParams={searchParams}
                                onPageChange={handlePageChange}
                                hasComparison={hasComparison}
                                comparedProducts={comparedProducts}
                                compareProductsLimit={compareProductsLimit}
                                onCompare={compareHandler}
                                onDragStart={handleDragStart}
                                onItemClick={handleItemClick}
                            />
                        )}
                    </section>
                </ProductsAndFacetsContainer>
            </PageGrid>
            {hasComparison && (
                <ComparedProducts
                    segment={segment}
                    categoryPath={categoryPath}
                    productsLimit={compareProductsLimit}
                    products={comparedProducts}
                    onAddProduct={addComparedProduct}
                    onRemoveProduct={removeComparedProduct}
                    onRemoveAllProducts={removeAllComparedProducts}
                />
            )}
            <DescriptionWithContentSlotContainer>
                {categoryData.description && <CategoryDescription description={categoryData.description} />}
                <ContentSlot identifier={categoryData.code} />
            </DescriptionWithContentSlotContainer>
            <SessionProlongationModal />
        </div>
    );
};
